import { modelLabel } from "@/lib/compensationModelLabels";
import { feeTypeLabel } from "@/lib/feeTypeLabels";
import { normalizeSupport } from "@/lib/orgSupport";
import { resolveLeadGenerationAnswer, type LeadGenerationFields } from "@/lib/orgLeadGeneration";
import type { OrgStoryView, StorySectionKey } from "@/lib/orgStory/types";

/**
 * The story rail's "At a glance" rows (spec 2026-10-01 §4): up to six
 * one-line facts about the offer, read from the same camelCase profile data
 * the sections below render, so both audiences get the same card.
 *
 * A row with nothing to say is absent, and so is an explicit "no" — the
 * unselected-items rule. Model and fee values are categorical labels only.
 */

export type GlanceRowKey = "model" | "fees" | "leads" | "training" | "support" | "benefits";

export interface GlanceRow {
  key: GlanceRowKey;
  label: string;
  value: string;
  /** The section that carries the detail. */
  section: StorySectionKey;
}

const strings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((s): s is string => typeof s === "string" && s.trim() !== "") : [];

const text = (v: unknown): string | null => (typeof v === "string" && v.trim() !== "" ? v.trim() : null);

/** "A, B +N more": the first two, then a count. */
const firstTwo = (labels: string[]): string => {
  const head = labels.slice(0, 2).join(", ");
  return labels.length > 2 ? `${head} +${labels.length - 2} more` : head;
};

/** Selected-benefit labels the two named flags already cover. */
const NAMED_BENEFIT = /health insurance|retirement/i;

export function glanceRows(view: OrgStoryView): GlanceRow[] {
  const pd = view.profileData;
  const rows: GlanceRow[] = [];

  const models = strings(pd.compensationModels);
  if (models.length > 0) {
    const primary = text(pd.primaryCompensationModel);
    const value = primary && models.includes(primary) ? modelLabel(primary) : firstTwo(models.map(modelLabel));
    rows.push({ key: "model", label: "Model", value, section: "offer" });
  }

  const fees = strings(pd.feeTypes);
  if (pd.noRecurringFees === true) {
    rows.push({ key: "fees", label: "Fees", value: "No recurring fees", section: "offer" });
  } else if (fees.length > 0) {
    rows.push({ key: "fees", label: "Fees", value: firstTwo(fees.map(feeTypeLabel)), section: "offer" });
  }

  if (resolveLeadGenerationAnswer(pd as LeadGenerationFields) === "provided") {
    const structured = Array.isArray(pd.leadSourcesStructured)
      ? strings(pd.leadSourcesStructured.map((s) => (s as { name?: unknown } | null)?.name))
      : [];
    const count = (structured.length > 0 ? structured : strings(pd.leadSources)).length;
    const value = count > 0 ? `Provided · ${count} ${count === 1 ? "source" : "sources"}` : "Provided";
    rows.push({ key: "leads", label: "Leads", value, section: "offer" });
  }

  const cadence = text(pd.trainingFrequency);
  const coaching = pd.oneOnOneCoaching === true ? "1:1 coaching" : null;
  const training = [cadence, coaching].filter(Boolean).join(" · ");
  if (training) rows.push({ key: "training", label: "Training", value: training, section: "growth" });

  const supportData = normalizeSupport(pd);
  const support = [
    supportData.transactionCoordinator ? "Transaction coordinator" : null,
    supportData.marketingSupport.length > 0 ? "Marketing support" : null,
  ].filter(Boolean);
  if (support.length > 0) {
    rows.push({ key: "support", label: "Support", value: support.join(", "), section: "operations" });
  }

  const named = [
    pd.hasHealthInsurance === true ? "Health insurance" : null,
    pd.hasRetirementPlan === true ? "Retirement plan" : null,
  ].filter(Boolean);
  const others = strings(pd.selectedBenefits).filter((b) => !NAMED_BENEFIT.test(b)).length;
  if (named.length > 0) {
    const value = named.join(", ") + (others > 0 ? ` +${others} more` : "");
    rows.push({ key: "benefits", label: "Benefits", value, section: "offer" });
  } else if (others > 0) {
    const value = `${others} ${others === 1 ? "benefit" : "benefits"}`;
    rows.push({ key: "benefits", label: "Benefits", value, section: "offer" });
  }

  return rows;
}
